/**
 * ============================================================================
 * DASHBOARD SLICE — global UI state: mode, week selection, filters.
 * ============================================================================
 *
 * PERSISTENCE: Stored to localStorage (versioned key). On reload, selections
 * restore — user doesn't lose their Live/Snapshot mode or chosen week.
 *
 * FILTERS: Live in Redux, applied client-side via Arquero. NOT part of the
 * RTK Query cache key — filter changes are instant, no network call.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DashboardFilters,
  EMPTY_FILTERS,
  NetworkId,
  WeekWindow,
} from 'src/types';
import { CalloutSelection } from 'src/types/metricComments.types';
import { WBR_DASHBOARD_ID } from 'src/constants';
import { DEFAULT_FILTER_QUERY } from 'src/config/filterConfig';
import { DEFAULT_SELECTED_DEMAND_CATEGORIES } from 'src/config/demandCategories';

// v7: the Last X / Forward X week controls were removed — window sizes are now
// fixed (last 4 + mode-derived forward), the user only picks the reporting
// week. Persisted lastXWeeks/forwardXWeeks are dropped; the version bump stops
// a returning user's stale window from being read.
// v6: "WBR Metrics Only" is a PRESET, not a Metric Group — the default
// metricGroup chip is gone (Metric Group defaults to All; the WBR-preset
// restriction lives ONLY on the home page as its hard scope). A returning
// user's persisted v5 default would keep restricting every page, so the
// version bump is required, not optional.
// v5: BYOT/Offline DCR defaults to Yes+No (the live dashboard does not exclude
// it); v4 persisted a single 'No' chip that dropped those DCRs and made every
// total lower than QuickSight's.
const STORAGE_KEY = 'wbr_dashboard_state_v7';

export interface FilterQuery {
  tokens: Array<{ propertyKey: string; operator: string; value: string }>;
  operation: 'and' | 'or';
}

export interface DashboardState {
  /**
   * The served dashboard this UI is bound to ("fai", "quality", …) — spec
   * multi-dashboard R2/R3. Joins every data call (metrics, definitions,
   * comments); caches key on it, so switching never evicts the other
   * dashboard's data. KFE (wire id "fai") is the default; the switcher guards persisted ids
   * that are no longer served.
   */
  activeDashboardId: string;
  activeNetwork: NetworkId;
  activeView: string;
  weekWindow: WeekWindow;
  filters: DashboardFilters;
  filterQuery: FilterQuery;
  selectedNetworkDrilldown: NetworkId | null;
  dataMode: 'live' | 'snapshot';
  /**
   * THE reporting week, shared by every page (null = current week live /
   * last completed week snapshot). Written by the header week picker on
   * every data page (DataModeSelector); read everywhere a window
   * or a displayed week resolves — one pick carries across pages like the
   * filters do (spec domains-page-week-picker R3.1). The ONLY window
   * control — Last/Forward X are fixed (see resolveWindow); window sizes
   * derive from mode + this anchor.
   */
  reportingWeek: string | null;
  /** DEMAND_CATEGORIES whose section groups render on the Detailed Summary tab (QuickSight checkbox row). */
  selectedDemandCategories: NetworkId[];
  /**
   * Show the All Networks section group — the standard three tables aggregated
   * across every network (spec all-networks-section-group R1/R3).
   */
  showAllNetworks: boolean;
  /**
   * The metric cell whose callout thread the unified panel shows (Metric tab).
   * Page-level so a grid cell click in ANY section drives the single shared
   * panel. Ephemeral — never persisted. null = panel shows the week list only.
   */
  selectedCalloutCell: CalloutSelection | null;
  /**
   * Whether the WBR Agent chat panel is open. The launcher (in the page header
   * controls row) and the docked panel (app-level) are separate components, so
   * this open state lifts to the store. Ephemeral — never persisted.
   */
  chatAgentOpen: boolean;
}

// ──── localStorage persistence (versioned, with schema guard) ────

interface PersistedFields {
  activeNetwork?: NetworkId;
  activeView?: string;
  weekWindow?: WeekWindow;
  filters?: DashboardFilters;
  filterQuery?: FilterQuery;
  dataMode?: 'live' | 'snapshot';
  reportingWeek?: string | null;
  selectedDemandCategories?: NetworkId[];
  showAllNetworks?: boolean;
  /** Legacy key (pre all-networks-section-group rename) — read-only fallback. */
  showAllNetworksOtd?: boolean;
}

function loadPersisted(): PersistedFields {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as PersistedFields;
  } catch {
    return {};
  }
}

const persisted = loadPersisted();

const initialState: DashboardState = {
  // Deliberately NOT persisted: every session starts on the default (KFE)
  // dashboard (reviewer decision 2026-08-21, superseding the persisted
  // selection — see spec multi-dashboard R2.4/decisions log).
  activeDashboardId: WBR_DASHBOARD_ID,
  activeNetwork: persisted.activeNetwork ?? 'all',
  activeView: persisted.activeView ?? 'detailed-summary',
  weekWindow: persisted.weekWindow ?? { from: '2026-W25', to: '2026-W29' },
  filters: persisted.filters ?? EMPTY_FILTERS,
  filterQuery: persisted.filterQuery ?? DEFAULT_FILTER_QUERY,
  selectedNetworkDrilldown: null,
  // Snapshot is the default first view for faster first paint (see spec
  // domains-page-week-picker R3.7); a persisted choice still wins.
  dataMode: persisted.dataMode ?? 'snapshot',
  reportingWeek: persisted.reportingWeek ?? null,
  selectedDemandCategories:
    persisted.selectedDemandCategories ?? DEFAULT_SELECTED_DEMAND_CATEGORIES,
  // Legacy persisted key read as fallback so the rename keeps a user's toggle
  // (spec all-networks-section-group R3.2); only the new key is written back.
  showAllNetworks:
    persisted.showAllNetworks ?? persisted.showAllNetworksOtd ?? false,
  selectedCalloutCell: null,
  chatAgentOpen: false,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    networkSelected(state, action: PayloadAction<NetworkId>) {
      state.activeNetwork = action.payload;
      state.selectedNetworkDrilldown = null;
    },
    viewSelected(state, action: PayloadAction<string>) {
      state.activeView = action.payload;
    },
    weekWindowChanged(state, action: PayloadAction<WeekWindow>) {
      state.weekWindow = action.payload;
    },
    filterChanged(
      state,
      action: PayloadAction<{
        key: keyof DashboardFilters;
        value: DashboardFilters[keyof DashboardFilters];
      }>,
    ) {
      (state.filters as Record<string, unknown>)[action.payload.key] =
        action.payload.value;
    },
    filtersReplaced(state, action: PayloadAction<DashboardFilters>) {
      state.filters = action.payload;
    },

    /** "Clear filters" button — removes ALL active filters including defaults. */
    filtersCleared(state) {
      state.filters = {
        ...EMPTY_FILTERS,
        period: '',
        metricGroup: 'all',
      };
    },
    drilldownOpened(state, action: PayloadAction<NetworkId>) {
      state.selectedNetworkDrilldown = action.payload;
    },
    drilldownClosed(state) {
      state.selectedNetworkDrilldown = null;
    },
    filterQueryChanged(state, action: PayloadAction<FilterQuery>) {
      state.filterQuery = action.payload;
    },
    dataModeChanged(state, action: PayloadAction<'live' | 'snapshot'>) {
      state.dataMode = action.payload;
      // reportingWeek is deliberately KEPT (spec domains-page-week-picker
      // R2.3): windows re-resolve for the new mode at read time
      // (resolveAnchor clamps a pick the mode can't anchor on), so there is
      // no window state to reset here.
    },
    /** The shared reporting-week anchor (null = current/last-completed). */
    reportingWeekChanged(state, action: PayloadAction<string | null>) {
      state.reportingWeek = action.payload;
    },
    /** Replace the checkbox selection wholesale (tile drill-down, legacy /network/:id landing). */
    demandCategoriesReplaced(state, action: PayloadAction<NetworkId[]>) {
      state.selectedDemandCategories = action.payload;
    },
    /** Detailed Summary demand-category checkbox toggled (QuickSight parity row). */
    demandCategoryToggled(state, action: PayloadAction<NetworkId>) {
      const id = action.payload;
      state.selectedDemandCategories = state.selectedDemandCategories.includes(
        id,
      )
        ? state.selectedDemandCategories.filter((n) => n !== id)
        : [...state.selectedDemandCategories, id];
    },
    /** "All Networks" checkbox — the cross-network section group (R3.1). */
    allNetworksToggled(state, action: PayloadAction<boolean>) {
      state.showAllNetworks = action.payload;
    },
    /**
     * Switch the served dashboard (header switcher — spec multi-dashboard
     * R2.3/R3.3). Filters and week window are KEPT (provisional decision,
     * 2026-08-21 — see the spec's decisions log); the callout cell selection
     * CLEARS because it references a metric of the previous dashboard.
     */
    dashboardChanged(state, action: PayloadAction<string>) {
      state.activeDashboardId = action.payload;
      state.selectedCalloutCell = null;
    },
    /** Select a metric cell for the unified callout panel (Metric tab). */
    calloutCellSelected(state, action: PayloadAction<CalloutSelection>) {
      state.selectedCalloutCell = action.payload;
    },
    /** Clear the selected cell — the panel falls back to the week list. */
    calloutCellCleared(state) {
      state.selectedCalloutCell = null;
    },
    /** Open the WBR Agent chat panel (launcher in the header controls row). */
    chatAgentOpened(state) {
      state.chatAgentOpen = true;
    },
    /** Close the WBR Agent chat panel. */
    chatAgentClosed(state) {
      state.chatAgentOpen = false;
    },
  },
});

export const {
  networkSelected,
  viewSelected,
  weekWindowChanged,
  filterChanged,
  filtersReplaced,
  filtersCleared,
  drilldownOpened,
  drilldownClosed,
  filterQueryChanged,
  dataModeChanged,
  reportingWeekChanged,
  demandCategoriesReplaced,
  demandCategoryToggled,
  allNetworksToggled,
  dashboardChanged,
  calloutCellSelected,
  calloutCellCleared,
  chatAgentOpened,
  chatAgentClosed,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;

/** Persist only on dashboard slice action dispatches (not RTK Query internals). */
let lastPersisted = '';
export function persistDashboardState(state: DashboardState): void {
  const toStore: PersistedFields = {
    activeNetwork: state.activeNetwork,
    activeView: state.activeView,
    weekWindow: state.weekWindow,
    filters: state.filters,
    filterQuery: state.filterQuery,
    dataMode: state.dataMode,
    reportingWeek: state.reportingWeek,
    selectedDemandCategories: state.selectedDemandCategories,
    showAllNetworks: state.showAllNetworks,
  };
  const json = JSON.stringify(toStore);
  if (json === lastPersisted) return; // skip unchanged writes
  lastPersisted = json;
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* quota exceeded */
  }
}
